import { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { API_ENDPOINTS } from "../utils/urls";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function Analytics() {
  const [ordersPerDay, setOrdersPerDay] = useState<{ date: string; value: number }[]>([]);
  const [totalsPerDay, setTotalsPerDay] = useState<{ date: string; value: number }[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30); // Default to 30 days

  const getAnalyticsData = async (period: number = 30) => {
    try {
      const res = await api.get(`${API_ENDPOINTS.DASHBOARD_ANALYTICS}?period=${period}`);
      const data = res.data?.data || {};
      setOrdersPerDay(data.ordersPerDay || []);
      setTotalsPerDay(data.totalsPerDay || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  useEffect(() => {
    getAnalyticsData(selectedPeriod);
  }, [selectedPeriod]);

  const handlePeriodChange = (period: number) => {
    setSelectedPeriod(period);
  };

  const totalOrders = useMemo(() => ordersPerDay.reduce((s, d) => s + (d.value || 0), 0), [ordersPerDay]);
  const totalAmount = useMemo(() => totalsPerDay.reduce((s, d) => s + (d.value || 0), 0), [totalsPerDay]);
  const avgOrder = useMemo(() => (totalOrders ? totalAmount / totalOrders : 0), [totalAmount, totalOrders]);

  return (
    <div>
      <div className="p-8">
        <Navbar />
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-8 space-y-4 lg:space-y-0 pt-3">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2 text-gray-900">
              Analytics Dashboard
            </h1>
            <p className="text-lg text-gray-600">
              Track your business performance with detailed analytics and insights.
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3 py-2 border border-blue-100 shadow-md">
            <div className="flex items-center space-x-1.5">
              <div className="bg-blue-100 rounded-full p-1.5">
                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <label htmlFor="period-select" className="text-xs font-semibold text-blue-800 whitespace-nowrap">
                Period:
              </label>
            </div>
            <div className="relative">
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(Number(e.target.value))}
                className="appearance-none bg-white border border-blue-200 hover:border-blue-400 focus:border-blue-500 rounded-lg px-2.5 py-1.5 pr-7 text-xs font-medium text-blue-800 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-100 cursor-pointer min-w-[90px] hover:shadow-md"
              >
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            title="Total Orders"
            value={String(totalOrders)}
            gradientFrom="#6366f1"
            gradientTo="#a78bfa"
          />
          <KpiCard
            title="Total Amount"
            value={`₹${totalAmount.toFixed(2)}`}
            gradientFrom="#10b981"
            gradientTo="#34d399"
          />
          <KpiCard
            title="Avg Order Value"
            value={`₹${avgOrder.toFixed(2)}`}
            gradientFrom="#f59e0b"
            gradientTo="#fbbf24"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Number of orders per day (last {selectedPeriod} days)
            </h2>
            <SimpleBarChart
              data={ordersPerDay}
              height={180}
              gradientFrom="#6366f1"
              gradientTo="#a78bfa"
              showValues
            />
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">
              Total amount per day (last {selectedPeriod} days)
            </h2>
            <SimpleBarChart
              data={totalsPerDay}
              height={180}
              gradientFrom="#10b981"
              gradientTo="#34d399"
              showValues
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

type BarDatum = { date: string; value: number };

function SimpleBarChart({
  data,
  height = 160,
  gradientFrom = "#6366f1",
  gradientTo = "#a78bfa",
  showValues = false,
}: {
  data: BarDatum[];
  height?: number;
  gradientFrom?: string;
  gradientTo?: string;
  showValues?: boolean;
}) {
  const max = useMemo(() => {
    return data.reduce((m, d) => (d.value > m ? d.value : m), 0) || 1;
  }, [data]);

  return (
    <div className="w-full border rounded p-3 bg-white">
      <div className="w-full" style={{ height }}>
        <div className="flex items-end gap-1 h-full">
          {data.map((d) => {
            const barHeight = Math.max(2, Math.round((d.value / max) * (height - 24)));
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center">
                {showValues && d.value > 0 ? (
                  <div className="text-[10px] text-gray-700 mb-1">{d.value}</div>
                ) : (
                  <div className="mb-1" />
                )}
                <div
                  className="w-full"
                  style={{
                    height: barHeight,
                    backgroundImage: `linear-gradient(180deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
                    borderRadius: 3,
                  }}
                  title={`${d.date}: ${d.value}`}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500 flex justify-between">
        <span>{data[0]?.date || ""}</span>
        <span>{data[data.length - 1]?.date || ""}</span>
      </div>
    </div>
  );
}

function KpiCard({ title, value, gradientFrom, gradientTo }: { title: string; value: string; gradientFrom: string; gradientTo: string }) {
  return (
    <div
      className="rounded text-white p-4 shadow"
      style={{
        backgroundImage: `linear-gradient(90deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
      }}
    >
      <div className="text-sm opacity-90">{title}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
